import { useState } from 'react'
import { SortOrder, SortConfig } from '.'

interface ChildProps<TData> {
  readonly sorted: TData[]
  readonly onSort: (sortConfig: SortConfig<TData>) => void
  readonly activeSortConfig?: SortConfig<TData>
}

interface Props<TData> {
  readonly data: TData[]
  readonly children: (props: ChildProps<TData>) => JSX.Element
  readonly defaultSortConfig: SortConfig<TData>
}

export function GenericSorter<TData>({
  data,
  children,
  defaultSortConfig
}: Props<TData>) {
  const [activeSortConfig, setActiveSortConfig] = useState<
    SortConfig<TData> | undefined
  >(defaultSortConfig)

  const sorted = !!activeSortConfig
    ? doSort<TData>(data, activeSortConfig)
    : data

  return children({
    sorted,
    onSort: (sortConfig) => {
      const updated = updateActiveSortConfigs(sortConfig, activeSortConfig)
      setActiveSortConfig(updated)
    },
    activeSortConfig
  })
}

export function updateActiveSortConfigs<TData>(
  sortConfig: SortConfig<TData>,
  activeSortConfig?: SortConfig<TData>
) {
  if (typeof activeSortConfig === 'undefined') {
    return sortConfig
  }

  const fieldMatches = activeSortConfig.field === sortConfig.field

  if (!fieldMatches) {
    return sortConfig
  }

  if (activeSortConfig.order === sortConfig.order) {
    return activeSortConfig
  }

  return sortConfig
}

function getValue<TData>(sortConfig: SortConfig<TData>, data: TData) {
  return !!sortConfig.getValue
    ? sortConfig.getValue(data)
    : data[sortConfig.field]
}

function predefinedSort<TData>(sortConfig: SortConfig<TData>) {
  return (sA: TData, sB: TData) => {
    const a = getValue(sortConfig, sA)
    const b = getValue(sortConfig, sB)
    if (a > b) {
      return 1
    }
    if (a < b) {
      return -1
    }
    return 0
  }
}

export function doSort<TData>(rowData: TData[], sortConfig: SortConfig<TData>) {
  const sortAsc =
    typeof sortConfig.sortAsc === 'undefined'
      ? predefinedSort<TData>(sortConfig)
      : sortConfig.sortAsc
  const sortedAsc = rowData.sort(sortAsc)
  return sortConfig.order === SortOrder.ASC ? sortedAsc : sortedAsc.reverse()
}
